import React, { useState, useEffect, useRef } from 'react';
import { CartItem } from '../types';
import { gsap } from 'gsap';

const getFullImageUrl = (url: string | null | undefined): string => {
  if (!url) return 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=600&q=80';
  if (url.startsWith('http')) return url;
  const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
  return `${apiBase}${url}`;
};

interface CheckoutProps {
  cart: CartItem[];
  onConfirm: (paymentMethod: string, deliveryAddress: string, subtotal: number, importeEnvio: number, tarifaServicio: number) => void;
  onBack: () => void;
  cartTotal?: number;
  includeDelivery?: boolean;
  includeServiceFee?: boolean;
  deliveryFee?: number;
  serviceFee?: number;
}

const Checkout: React.FC<CheckoutProps> = ({ cart, onConfirm, onBack, cartTotal, includeDelivery = false, includeServiceFee = false, deliveryFee = 40, serviceFee = 15 }) => {
  const [step] = useState(1);
  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const total = cartTotal ?? subtotal;
  const deliveryAddress = localStorage.getItem('deliveryAddress') || '';
  const deliveryPhone = localStorage.getItem('deliveryPhone') || '';

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".checkout-section",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="max-w-[1200px] mx-auto pt-20 pb-32 px-6 lg:px-24">
      <div className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
        <button onClick={onBack} className="hover:text-primary transition-colors">Carrito</button>
        <span className="material-symbols-outlined text-sm">chevron_right</span>
        <span className="text-primary">Finalizar Pedido</span>
      </div>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
        <div>
          <h1 className="text-4xl md:text-5xl font-black text-secondary dark:text-white tracking-tight leading-tight">Finalizar Pedido</h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 px-6 py-3 bg-white/50 dark:bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm shadow-premium-hover">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-black text-xs">1</div>
            <span className="text-sm font-black text-secondary dark:text-white">Entrega</span>
            <div className="w-8 h-px bg-gray-200 dark:bg-white/10 mx-2"></div>
            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-400 font-black text-xs">2</div>
            <span className="text-sm font-black text-gray-400">Pago</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7 space-y-10">
          <section className="checkout-section">
            <h3 className="text-2xl font-black text-secondary dark:text-white mb-6 flex items-center gap-4">
              <span className="w-10 h-10 rounded-2xl bg-orange-100 dark:bg-orange-500/10 flex items-center justify-center text-primary">
                <span className="material-symbols-outlined font-black">location_on</span>
              </span>
              Dirección de Entrega
            </h3>

            <div className="glass-card rounded-[2.5rem] p-8 border-2 border-primary shadow-premium shadow-premium-hover">
              <div className="flex items-start gap-6">
                <div className="w-6 h-6 rounded-full border-4 border-primary flex items-center justify-center shrink-0 mt-1 ">
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                </div>
                <div className="flex-1">
                  {deliveryAddress ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-black uppercase tracking-wider">Entrega</span>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed">
                        {deliveryAddress}
                      </p>
                      {deliveryPhone && (
                        <p className="text-gray-500 dark:text-gray-400 font-medium mt-2 flex items-center gap-2">
                          <span className="material-symbols-outlined text-sm text-primary">phone</span>
                          {deliveryPhone.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3')}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 font-medium italic">No has ingresado una dirección de entrega</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="checkout-section">
            <h3 className="text-2xl font-black text-secondary dark:text-white mb-6 flex items-center gap-4 ">
              <span className="w-10 h-10 rounded-2xl bg-blue-100 dark:bg-blue-500/10 flex items-center justify-center text-blue-500">
                <span className="material-symbols-outlined font-black">payments</span>
              </span>
              Método de Pago
            </h3>

            <div className="glass-card rounded-[2.5rem] p-8 flex items-center justify-between group hover:border-primary/30 transition-all cursor-pointer shadow-premium-hover dark:bg-slate-800/50 dark:rounded-[2.5rem] bg-gray-200">
              <div className="flex items-center gap-6">
                <div className="w-16 h-12 rounded-xl bg-gray-50 dark:bg-black/20 flex items-center justify-center shadow-inner">
                  <span className="material-symbols-outlined text-3xl text-gray-400">credit_card</span>
                </div>
                <div>
                  <h4 className="font-black text-secondary dark:text-white">Visa terminada en 4242</h4>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Expira 12/25</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-300 group-hover:text-primary transition-colors">chevron_right</span>
            </div>
          </section>

          <div className="checkout-section pt-8 shadow-premium-hover rounded-[2.5rem] bg-green-600 dark:bg-green-500/50 dark:hover:bg-green-600 hover:bg-green-700 text-white">
            <button
              onClick={() => onConfirm('Visa terminada en 4242', deliveryAddress, subtotal, includeDelivery ? deliveryFee : 0, includeServiceFee ? serviceFee : 0)}
              className="btn-primary-premium w-full py-6 text-lg group"
            >
              <span className="font-bold">Confirmar y Pagar ${total.toFixed(0)}</span>
              <span className="material-symbols-outlined group-hover:scale-125 transition-transform">bolt</span>
            </button>
            <p className="mt-6 text-center text-xs font-medium text-white px-10 pb-2">
              Al completar tu pedido, aceptas nuestros Términos de Servicio y Políticas de Privacidad.
            </p>
          </div>
        </div>

        <div className="lg:col-span-5 rou">
          <div className="p-6 checkout-section sticky top-24 glass-card rounded-[3rem] dark:bg-slate-800/50 dark:rounded-[2.5rem]">
            <h2 className="text-2xl font-black text-secondary dark:text-white mb-10">Tu Pedido</h2>

            <div className="space-y-6 mb-10 max-h-[400px] overflow-y-auto  custom-scrollbar shadow-premium-hover rounded-[2.5rem] p-6 bg-gray-200 dark:bg-slate-800 dark:p-6 ">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4 items-center">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0">
                    <img src={getFullImageUrl(item.image)} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="text-sm font-black text-secondary dark:text-white leading-tight">{item.name}</p>
                      <span className="text-sm font-black text-primary ml-4">${(item.price * item.quantity).toFixed(0)}</span>
                    </div>
                    <p className="text-xs font-bold text-gray-400 mt-1">{item.quantity} Unidad(es)</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4 pt-8 border-t border-gray-100 dark:border-white/5 mb-8 shadow-premium-hover rounded-[2.5rem] p-6 bg-gray-200 dark:bg-slate-800">
              <div className="flex justify-between text-sm font-bold text-gray-500">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(0)}</span>
              </div>
              {includeServiceFee && (
                <div className="flex justify-between text-sm font-bold text-gray-500">
                  <span>Tarifa de servicio</span>
                  <span>${serviceFee.toFixed(0)}</span>
                </div>
              )}
              {includeDelivery && (
                <div className="flex justify-between text-sm font-bold text-gray-500">
                  <span>Costo de envío</span>
                  <span>${deliveryFee.toFixed(0)}</span>
                </div>
              )}
              <div className="flex justify-between items-end pt-4">
                <span className="text-xl font-black text-secondary dark:text-white">Total Final</span>
                <span className="text-3xl font-black text-primary">${total.toFixed(0)}</span>
              </div>
            </div>

            <div className="p-4 rounded-3xl bg-green-100 dark:bg-green-500/20 border border-green-100 dark:border-green-500/20 flex items-center gap-4 shadow-premium-hover">
              <div className="w-10 h-10 rounded-2xl bg-white dark:bg-black/20 flex items-center justify-center text-green-500 shadow-sm">
                <span className="material-symbols-outlined font-black">check_circle</span>
              </div>
              <p className="text-xs font-black text-green-700 dark:text-green-500 uppercase tracking-widest leading-relaxed">
                Entrega Prioritaria Activada
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
